import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { toast } from "sonner";
import { CheckCircle, Crown, Flame } from "@phosphor-icons/react";
import Footer from "@/components/Footer";

function CheckoutCta({ tier, soldOut, loading, onCheckout }) {
  if (tier?.pro) {
    return (
      <div className="mt-6 p-3 border border-[#00e676] text-[#00e676] font-mono-data text-xs" data-testid="pro-active">
        PRO ACTIVE · expires {tier.pro_until?.slice(0, 10)}
      </div>
    );
  }
  if (soldOut) {
    return (
      <button
        disabled
        data-testid="checkout-soldout"
        className="mt-6 w-full py-3 bg-[#1a1a1a] text-[#555] text-sm font-medium tracking-widest uppercase cursor-not-allowed"
      >
        Sold Out
      </button>
    );
  }
  return (
    <button
      onClick={onCheckout}
      disabled={loading}
      data-testid="checkout-btn"
      className="mt-6 w-full py-3 bg-[#f2ce72] text-black hover:bg-white text-sm font-medium tracking-widest uppercase disabled:opacity-50 transition-colors"
    >
      {loading ? "..." : "Claim spot · $49 lifetime"}
    </button>
  );
}

export default function Upgrade() {
  const [tier, setTier] = useState(null);
  const [founding, setFounding] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/me/tier").then(r => setTier(r.data)).catch(() => {});
    api.get("/founding-status").then(r => setFounding(r.data)).catch(() => {});
  }, []);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: "founding_lifetime",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  const claimed = founding?.claimed ?? 0;
  const cap = founding?.cap ?? 50;
  const remaining = founding?.remaining ?? cap;
  const soldOut = founding?.sold_out;
  const pct = Math.min(100, Math.round((claimed / cap) * 100));

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopBar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="micro-label">/ founding member offer</div>
        <h1 className="font-heading text-4xl mt-2 mb-3">
          <span className="text-[#f2ce72]">Founding</span> Member
        </h1>
        <p className="text-sm text-[#8a8a8e] font-mono-data mb-8 max-w-xl">
          Lifetime access to Pro for a single payment. First 50 users only — after that,
          Pro becomes $9/month. Your grandfathered rate: nothing forever.
        </p>

        {/* Scarcity bar */}
        <div className="panel p-4 mb-6" data-testid="founding-status">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Flame size={14} weight="fill" className="text-[#ff3b30]"/>
              <span className="micro-label text-[#ff3b30]">
                {soldOut ? "SOLD OUT" : `${remaining} / ${cap} SPOTS REMAINING`}
              </span>
            </div>
            <span className="font-mono-data text-xs text-[#8a8a8e]">
              {claimed} claimed
            </span>
          </div>
          <div className="w-full h-1 bg-[#1a1a1a]">
            <div className="h-1 bg-[#ff3b30]" style={{ width: `${pct}%` }}/>
          </div>
        </div>

        {/* Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222] border border-[#222]">
          <div className="bg-[#0f0f0f] p-6">
            <div className="micro-label">Free</div>
            <div className="font-mono-data text-3xl mt-2 mb-6">$0</div>
            <ul className="space-y-2 text-sm text-[#8a8a8e]">
              <li>· 5 watchlist symbols</li>
              <li>· 3 AI insights / day</li>
              <li>· 1 backtest / day</li>
              <li>· Dashboard + signals + charts</li>
            </ul>
          </div>
          <div className="bg-[#0f0f0f] p-6 border-l-2 border-[#f2ce72] relative">
            <div className="absolute top-3 right-3 px-2 py-0.5 bg-[#f2ce72] text-black text-[9px] font-mono-data tracking-widest">
              LIMITED
            </div>
            <div className="flex items-center gap-2">
              <Crown size={14} weight="fill" className="text-[#f2ce72]"/>
              <span className="micro-label text-[#f2ce72]">Founding Member</span>
            </div>
            <div className="font-mono-data text-3xl mt-2 mb-1">
              $49<span className="text-base text-[#8a8a8e]"> · once</span>
            </div>
            <div className="text-[10px] font-mono-data text-[#8a8a8e] mb-4">
              lifetime access · no monthly billing
            </div>
            <ul className="space-y-2 text-sm mt-2">
              <li className="flex gap-2"><CheckCircle size={14} className="text-[#00e676] mt-0.5 shrink-0"/>Unlimited AI insights (Claude Sonnet 4.5)</li>
              <li className="flex gap-2"><CheckCircle size={14} className="text-[#00e676] mt-0.5 shrink-0"/>Unlimited backtests + custom rule builder</li>
              <li className="flex gap-2"><CheckCircle size={14} className="text-[#00e676] mt-0.5 shrink-0"/>AI Strategy Builder (multi-leg options)</li>
              <li className="flex gap-2"><CheckCircle size={14} className="text-[#00e676] mt-0.5 shrink-0"/>Unlimited watchlist & universe</li>
              <li className="flex gap-2"><CheckCircle size={14} className="text-[#00e676] mt-0.5 shrink-0"/>Every future Pro feature — forever</li>
              <li className="flex gap-2"><CheckCircle size={14} className="text-[#00e676] mt-0.5 shrink-0"/>🏴‍☠️ Founding Member badge</li>
            </ul>

            <CheckoutCta tier={tier} soldOut={soldOut} loading={loading} onCheckout={checkout} />
            <p className="mt-3 text-[10px] font-mono-data text-[#555]">
              Secure checkout via Stripe · One-time payment · No auto-renewal
            </p>
          </div>
        </div>

        <p className="mt-8 text-xs text-[#555] font-mono-data max-w-xl">
          After the 50 spots are claimed, Pro will move to $9/month subscription pricing.
          Founding members keep lifetime access at no additional cost, ever.
        </p>
      </div>
      <Footer />
    </div>
  );
}
